/**
 * Parse notification template JSON and read `message.message_data`
 * as a slug → label map for dispatch forms.
 */

/**
 * @param {string|object|null|undefined} raw
 * @returns {object|null}
 */
export function parseNotificationMessage(raw) {
  if (raw == null) return null
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw
  if (typeof raw !== 'string' || !raw.trim()) return null
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed
    }
    return null
  } catch (e) {
    return null
  }
}

/**
 * Resolve `message.message_data` from an admin model item.
 * Prefers nested JSON (`model.message.message_data`), then flat catalogs.
 *
 * @param {object|null|undefined} model
 * @returns {Record<string, string>} slug → label
 */
export function messageDataCatalogFromModel(model) {
  if (!model) return {}

  const fromMessage = parseNotificationMessage(model.message)
  const nested =
    fromMessage &&
    fromMessage.message_data &&
    typeof fromMessage.message_data === 'object' &&
    !Array.isArray(fromMessage.message_data)
      ? fromMessage.message_data
      : null

  const raw =
    nested ||
    (model.messageData && typeof model.messageData === 'object'
      ? model.messageData
      : null) ||
    (model.message_data && typeof model.message_data === 'object'
      ? model.message_data
      : null) ||
    {}

  const catalog = {}
  Object.keys(raw).forEach((slug) => {
    const label = raw[slug]
    if (typeof label === 'string') {
      catalog[slug] = label
    }
  })
  return catalog
}

/**
 * @param {object|null|undefined} model
 * @returns {{ slug: string, label: string }[]}
 */
export function listMessageDataFields(model) {
  const catalog = messageDataCatalogFromModel(model)
  return Object.keys(catalog).map((slug) => ({
    slug,
    label: catalog[slug] || slug,
  }))
}

/**
 * Replace `${slug}` placeholders with form values (simple keys only).
 * @param {string} text
 * @param {Record<string, string>} vars
 * @returns {string}
 */
export function interpolateString(text, vars) {
  if (typeof text !== 'string') return text
  const map = vars || {}
  return text.replace(/\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (_m, key) => {
    if (!Object.prototype.hasOwnProperty.call(map, key) || map[key] == null) {
      return ''
    }
    return String(map[key])
  })
}

/**
 * Deep-clone + interpolate string leaves with form vars.
 * @param {*} value
 * @param {Record<string, string>} vars
 * @returns {*}
 */
export function interpolateValue(value, vars) {
  if (typeof value === 'string') return interpolateString(value, vars)
  if (Array.isArray(value)) return value.map((item) => interpolateValue(item, vars))
  if (value && typeof value === 'object') {
    const out = {}
    Object.keys(value).forEach((key) => {
      out[key] = interpolateValue(value[key], vars)
    })
    return out
  }
  return value
}

/**
 * Build POST body message JSON from model template + form values.
 * @param {object|null|undefined} model
 * @param {Record<string, string>} formValues
 * @param {{ to: string, sendDate: string }} opts
 * @returns {object}
 */
export function buildDispatchMessage(model, formValues, opts) {
  const caption = parseNotificationMessage(model && model.message) || {}
  const vars = formValues || {}
  const built = interpolateValue(JSON.parse(JSON.stringify(caption)), vars)

  built.model_from = 'Admin Panel'
  built.model_to = opts && opts.to != null ? opts.to : built.model_to
  built.model_send_date =
    opts && opts.sendDate != null ? opts.sendDate : built.model_send_date
  built.model_status = 2
  built.message_data = { ...vars }

  if (built.model_message && typeof built.model_message === 'object') {
    if (Object.prototype.hasOwnProperty.call(built.model_message, 'number')) {
      built.model_message.number = opts.to
    }
    if (Object.prototype.hasOwnProperty.call(built.model_message, 'to')) {
      built.model_message.to = opts.to
    }
  }

  return built
}

/**
 * Title from interpolated model_title (strip leading "Modelo:" if present).
 * @param {object|null|undefined} model
 * @param {Record<string, string>} formValues
 * @returns {string}
 */
export function buildDispatchTitle(model, formValues) {
  const caption = parseNotificationMessage(model && model.message) || {}
  const raw = interpolateString(
    String(caption.model_title || (model && model.title) || ''),
    formValues || {}
  )
  if (!raw) return (model && model.title) || 'Disparo admin'
  if (raw.includes('Modelo')) {
    return raw
      .split(':')
      .slice(1)
      .join(':')
      .trim() || raw
  }
  return raw.trim()
}

/**
 * Channel from model / template.
 * @param {object|null|undefined} model
 * @returns {string}
 */
export function resolveModelChannel(model) {
  if (!model) return ''
  const caption = parseNotificationMessage(model.message) || {}
  return String(
    model.channel || caption.model_channel || model.model_channel || ''
  ).trim()
}

const PLACEHOLDER_START_RE = /\$\{([A-Za-z_][A-Za-z0-9_]*)/g

/**
 * Collect `${slug…}` identifiers from a value tree.
 * Skips the `message_data` catalog itself (labels, not content).
 * @param {*} value
 * @param {Set<string>} [out]
 * @param {string} [key]
 * @returns {Set<string>}
 */
export function collectContentVarNames(value, out = new Set(), key = '') {
  if (key === 'message_data') return out
  if (typeof value === 'string') {
    PLACEHOLDER_START_RE.lastIndex = 0
    let match
    while ((match = PLACEHOLDER_START_RE.exec(value)) !== null) {
      out.add(match[1])
    }
    return out
  }
  if (Array.isArray(value)) {
    if (key === 'variables') {
      value.forEach((item) => {
        if (typeof item === 'string' && item.trim()) out.add(item.trim())
      })
      return out
    }
    value.forEach((item) => collectContentVarNames(item, out))
    return out
  }
  if (value && typeof value === 'object') {
    Object.keys(value).forEach((childKey) => {
      collectContentVarNames(value[childKey], out, childKey)
    })
  }
  return out
}

/**
 * Vars referenced in model content (esp. model_message) but missing from message_data.
 * @param {object|null|undefined} model
 * @returns {string[]}
 */
export function findMissingContentVars(model) {
  const caption = parseNotificationMessage(model && model.message)
  if (!caption) return []
  const catalog = messageDataCatalogFromModel(model)
  const catalogKeys = new Set(Object.keys(catalog))
  // Prefer scanning model_message (+ sibling content fields except message_data)
  const referenced = collectContentVarNames(caption)
  return Array.from(referenced)
    .filter((name) => !catalogKeys.has(name))
    .sort()
}

/**
 * Vars referenced specifically inside `model_message` but absent from message_data.
 * @param {object|null|undefined} model
 * @returns {string[]}
 */
export function findUnmappedModelMessageVars(model) {
  const caption = parseNotificationMessage(model && model.message)
  if (!caption || !caption.model_message) return []
  const catalog = messageDataCatalogFromModel(model)
  const catalogKeys = new Set(Object.keys(catalog))
  const referenced = collectContentVarNames(caption.model_message)
  return Array.from(referenced)
    .filter((name) => !catalogKeys.has(name))
    .sort()
}

/**
 * Human-readable template text from `model_message` (channel-dependent shape).
 * @param {object|null|undefined} modelMessage
 * @returns {string}
 */
export function extractModelMessageBody(modelMessage) {
  if (modelMessage == null) return ''
  if (typeof modelMessage === 'string') {
    return modelMessage.replace(/\\n/g, '\n')
  }
  if (typeof modelMessage !== 'object') return String(modelMessage)

  const parts = []
  if (modelMessage.title != null && String(modelMessage.title).trim()) {
    parts.push(String(modelMessage.title))
  }
  if (modelMessage.subject != null && String(modelMessage.subject).trim()) {
    parts.push(String(modelMessage.subject))
  }
  const body =
    modelMessage.message != null
      ? modelMessage.message
      : modelMessage.caption != null
        ? modelMessage.caption
        : modelMessage.body != null
          ? modelMessage.body
          : null
  if (body != null && String(body).trim()) {
    parts.push(String(body))
  }
  if (!parts.length) {
    try {
      return JSON.stringify(modelMessage, null, 2)
    } catch (e) {
      return ''
    }
  }
  return parts.join('\n\n').replace(/\\n/g, '\n')
}

/**
 * Live preview: interpolate `model_message` placeholders with form values.
 * Unmatched `${slug}` stay empty (same as dispatch interpolate).
 * @param {object|null|undefined} model
 * @param {Record<string, string>} formValues
 * @returns {string}
 */
export function buildMessagePreview(model, formValues) {
  const caption = parseNotificationMessage(model && model.message)
  if (!caption) return ''
  const raw = extractModelMessageBody(caption.model_message)
  if (!raw) return ''
  return interpolateString(raw, formValues || {})
}
